/**
 * 用户管理服务 - 管理多个用户的 userUuid
 * 
 * 数据存储在 InfiniteNote 公共数据库中：
 * - currentUser: 当前用户 UUID
 * - users: 用户列表
 */

import { v4 as uuidv4 } from 'uuid'
import {
  saveUsers,
  getUsers,
  saveCurrentUser,
  getCurrentUser
} from './promptManagerDB'
import {
  checkUserDBExists,
  initUserDB,
  type InitializationStatus
} from './dbManager'

export interface UserInfo {
  userUuid: string
  nickname?: string // 用户昵称（可选）
  lastLoginTime: number // 最后登录时间
  createdAt: number // 创建时间
}

// 获取所有用户列表
export async function getAllUsers(): Promise<UserInfo[]> {
  try {
    const users = await getUsers()
    return users || []
  } catch (error) {
    console.error('获取用户列表失败:', error)
    return []
  }
}

// 从服务器同步用户信息到本地
export async function syncUserInfoFromServer(userUuid: string): Promise<void> {
  try {
    const { getUserInfoByUuid } = await import('./api')
    const response = await getUserInfoByUuid(userUuid)
    
    if (response.success && response.data) {
      const serverUserInfo = response.data
      // 更新本地用户信息（保留 lastLoginTime 和 createdAt）
      const users = await getAllUsers()
      const existingUserIndex = users.findIndex(u => u.userUuid === userUuid)
      
      if (existingUserIndex >= 0) {
        // 更新现有用户，保留本地的时间信息
        users[existingUserIndex] = {
          ...users[existingUserIndex],
          nickname: serverUserInfo.nickname || users[existingUserIndex].nickname
        }
      } else {
        // 如果本地不存在，添加新用户
        users.push({
          userUuid: serverUserInfo.userUuid,
          nickname: serverUserInfo.nickname,
          lastLoginTime: Date.now(),
          createdAt: Date.now()
        })
      }
      
      // 保存更新后的用户列表
      await saveUsersList(users)
      console.log('[syncUserInfoFromServer] 已从服务器同步用户信息:', userUuid, serverUserInfo.nickname)
    }
  } catch (error) {
    console.warn('[syncUserInfoFromServer] 从服务器同步用户信息失败:', error)
    // 失败不影响使用，只记录警告
  }
}

// 保存用户列表
async function saveUsersList(users: UserInfo[]): Promise<void> {
  try {
    await saveUsers(users)
  } catch (error) {
    console.error('保存用户列表失败:', error)
    throw error
  }
}

// 添加或更新用户
export async function addOrUpdateUser(userUuid: string, nickname?: string): Promise<void> {
  const users = await getAllUsers()
  const existingUserIndex = users.findIndex(u => u.userUuid === userUuid)
  
  const userInfo: UserInfo = {
    userUuid,
    nickname,
    lastLoginTime: Date.now(),
    createdAt: existingUserIndex >= 0 ? users[existingUserIndex].createdAt : Date.now()
  }
  
  if (existingUserIndex >= 0) {
    // 更新现有用户
    users[existingUserIndex] = userInfo
  } else {
    // 添加新用户
    users.push(userInfo)
  }
  
  await saveUsersList(users)
}

// 删除用户（从列表中删除，但保留数据）
export async function removeUser(userUuid: string): Promise<void> {
  const users = await getAllUsers()
  const filtered = users.filter(u => u.userUuid !== userUuid)
  await saveUsersList(filtered)
}

// 获取当前用户UUID
export async function getCurrentUserUuid(): Promise<string | null> {
  try {
    return await getCurrentUser()
  } catch (error) {
    console.error('获取当前用户失败:', error)
    return null
  }
}

// 设置当前用户UUID
export async function setCurrentUserUuid(userUuid: string): Promise<void> {
  try {
    await saveCurrentUser(userUuid)
    // 更新用户列表中的最后登录时间
    await addOrUpdateUser(userUuid)
  } catch (error) {
    console.error('设置当前用户失败:', error)
    throw error
  }
}

// 获取或创建用户UUID（如果不存在则创建新的）
export async function getOrCreateUserUuid(
  onProgress?: (status: InitializationStatus) => void
): Promise<string> {
  let userUuid = await getCurrentUserUuid()
  
  if (!userUuid) {
    // 如果没有当前用户，创建新用户
    userUuid = uuidv4()
    await setCurrentUserUuid(userUuid)
    await addOrUpdateUser(userUuid)
    
    // 为新用户创建数据库
    await initUserDB(userUuid, onProgress)
  } else {
    // 检查用户数据库是否存在
    const dbExists = await checkUserDBExists(userUuid)
    if (!dbExists) {
      // 如果数据库不存在，创建它
      await initUserDB(userUuid, onProgress)
    }
    // 更新最后登录时间
    await addOrUpdateUser(userUuid)
  }
  
  return userUuid
}

// 切换用户
export async function switchUser(
  userUuid: string,
  onProgress?: (status: InitializationStatus) => void
): Promise<void> {
  await setCurrentUserUuid(userUuid)
  await addOrUpdateUser(userUuid)
  
  // 检查用户数据库是否存在，不存在则创建
  const dbExists = await checkUserDBExists(userUuid)
  if (!dbExists) {
    await initUserDB(userUuid, onProgress)
  }
}

// 保存用户数据（现在只是更新用户信息，实际数据存储在用户独立数据库中）
export async function saveUserData(userUuid: string, _data: any): Promise<void> {
  // 用户数据现在存储在 IN_{uuid} 数据库中，这里只更新用户信息
  await addOrUpdateUser(userUuid)
}

// 获取用户数据（现在返回 null，实际数据从用户独立数据库读取）
export async function getUserData(_userUuid: string): Promise<any | null> {
  // 用户数据现在从 IN_{uuid} 数据库读取
  return null
}

// 删除用户数据
export async function deleteUserData(userUuid: string): Promise<void> {
  // 可以选择删除用户数据库
  try {
    const { deleteUserDB } = await import('./dbManager')
    await deleteUserDB(userUuid)
  } catch (error) {
    console.error('删除用户数据失败:', error)
  }
}

// 更新用户昵称（同步到本地和服务器）
export async function updateUserNickname(userUuid: string, nickname: string): Promise<void> {
  // 先更新本地存储
  await addOrUpdateUser(userUuid, nickname)
  
  // 同步到服务器
  try {
    const { updateUserInfoByUuid } = await import('./api')
    await updateUserInfoByUuid(userUuid, { nickname })
    console.log('[updateUserNickname] 用户昵称已同步到服务器')
  } catch (error) {
    console.warn('[updateUserNickname] 同步到服务器失败，但已保存到本地:', error)
    // 即使服务器同步失败，本地数据也已经保存了
  }
}

// 检查所有可能的本地存储位置，尝试找到 userUuid（用于迁移旧数据）
export async function checkLocalUserUuid(): Promise<string | null> {
  // 1. 检查当前标准存储位置（IndexedDB）
  const userUuid = await getCurrentUserUuid()
  if (userUuid) return userUuid

  // 不再检查 localStorage，旧数据迁移已完成
  return null
}

// 创建新用户并初始化数据库
export async function createNewUser(
  onProgress?: (status: InitializationStatus) => void
): Promise<string> {
  const userUuid = uuidv4()
  
  // 设置为当前用户
  await setCurrentUserUuid(userUuid)
  await addOrUpdateUser(userUuid)
  
  // 创建用户数据库
  await initUserDB(userUuid, onProgress)
  
  return userUuid
}

// 登录已有用户（检查本地和服务器）
export async function loginExistingUser(
  userUuid: string,
  onProgress?: (status: InitializationStatus) => void
): Promise<boolean> {
  // 检查本地是否有该用户的数据库
  const localDBExists = await checkUserDBExists(userUuid)
  
  if (localDBExists) {
    // 本地有数据库，直接切换
    await switchUser(userUuid, onProgress)
    return true
  }
  
  // 本地没有，检查服务器
  try {
    const { verifyUser } = await import('./api')
    const result = await verifyUser(userUuid)
    
    if (result.success && result.data?.exists) {
      // 服务器有该用户，创建本地数据库
      await setCurrentUserUuid(userUuid)
      await addOrUpdateUser(userUuid)
      await initUserDB(userUuid, onProgress)
      return true
    }
  } catch (error) {
    console.warn('验证用户失败:', error)
  }
  
  return false
}
